import React, { useState, useEffect, useMemo } from 'react';
import { Form, InputGroup } from 'react-bootstrap';
import { IIdentitiesPresaleApi } from '@api/identities-presale/identities-presale-api';
import { IdentityPriceRange } from '@api/identities-presale/types';
import * as yup from 'yup';
import { RoundButton } from '@/components/common/RoundButton';
import { Spinner } from '@/components/common';
import classNames from 'classnames';

interface Props {
  priceRanges: IdentityPriceRange[];
  presaleDiscount: number;
  identitiesList: { name: string, price: number, discountedPrice: number }[];
  identitiesPresaleApi: IIdentitiesPresaleApi;
  onAddIdentity: (payload: { name: string, price: number, discountedPrice: number }) => void;
}

const identityNameRegex = /^[a-zA-Z0-9]([a-zA-Z0-9]|(?!--)(?!__)(?!-_)(?!_-)[_-])*[a-zA-Z0-9]$/;

const validationSchema = yup.object().shape({
  identityName: yup
    .string()
    .matches(identityNameRegex, 'Identity name can only contain lowercase letters, numbers and underscores')
    .required('Identity name is required')
});

export const IdentitiesInput: React.FC<Props> = ({
  priceRanges,
  presaleDiscount,
  identitiesList,
  identitiesPresaleApi,
  onAddIdentity,
}) => {
  const [identityName, setIdentityName] = useState('');
  const [isAvailable, setIsAvailable] = useState<boolean | null>(null);
  const [isChecking, setIsChecking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [price, setPrice] = useState<number | null>(null);

  const discountedPrice = useMemo(() => {
    return price ? price * (1 - presaleDiscount) : null;
  }, [price, presaleDiscount]);

  useEffect(() => {
    const checkAvailability = async () => {
      if (!identityName) {
        setIsAvailable(null);
        setError(null);
        setPrice(null);
        return;
      }

      if (identitiesList.some(identity => identity.name === identityName)) {
        setIsAvailable(false);
        setError('This identity name is already added to the list');
        return;
      }

      try {
        await validationSchema.validate({ identityName });
        setError(null);
        setIsChecking(true);

        const result = await identitiesPresaleApi.checkIdentityName(identityName);
        
        if ('error' in result) {
          setError(result.error);
          setIsAvailable(false);
          return;
        }

        const { isAvailable: available } = result;

        setIsAvailable(available);

        if (!available) {
          setError('This identity name is already taken');
          return
        }
        
        const matchingRange = priceRanges.find(
          range => 
            identityName.length >= range.lengthFrom && 
            (!range.lengthTo || identityName.length <= range.lengthTo)
        );
        
        setPrice(matchingRange!.priceUsd)
      } catch (err) {
        if (err instanceof yup.ValidationError) {
          setError(err.message);
        } else {
          setError('Failed to check identity availability');
        }
        setIsAvailable(false);
        setPrice(null);
      } finally {
        setIsChecking(false);
      }
    };

    const timeoutId = setTimeout(checkAvailability, 500);
    return () => clearTimeout(timeoutId);
  }, [identityName]);

  const handleAdd = () => {
    if (!isAvailable || !price || !discountedPrice) {
      setError('This identity name is already taken');
      return;
    }

    onAddIdentity({ name: identityName, price, discountedPrice })
    setIdentityName('')
    setIsAvailable(null)
    setPrice(null)
  };

  return (
    <div className="identities-input">
      <Form.Group controlId="identityName">
        <Form.Label>Identity name</Form.Label>
        <InputGroup className={classNames("swap-form__input-group input-group-big", { "is-invalid": !!error })}>
          <Form.Control
            type="text"
            value={identityName}
            onChange={(e) => setIdentityName(e.target.value.toLowerCase())}
            placeholder="Enter identity name"
            isInvalid={!!error}
          />
          {isChecking ? (
            <InputGroup.Append>
              <Spinner />
            </InputGroup.Append>
          ) : isAvailable && price !== null && discountedPrice !== null ? (
            <InputGroup.Append>
              <span className="text-decoration-line-through">${price.toFixed(2)}</span>{' '}
              <big className="purple-text">${discountedPrice.toFixed(2)}</big>
            </InputGroup.Append>
          ) : null}
          <InputGroup.Append>
            <RoundButton size="small" color="DARK" onClick={handleAdd} disabled={!isAvailable || isChecking || !price}>
              Add
            </RoundButton>
          </InputGroup.Append>
        </InputGroup>
        <Form.Control.Feedback type="invalid">{error}</Form.Control.Feedback>
      </Form.Group>
    </div>
  )
}
